import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import api from "@/lib/axios"
import FormHeader from "@/components/FormHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useState } from "react"
import { FaPlus } from "react-icons/fa6"
import { Loader2 } from "lucide-react"
import { JalurJenis, TahapanCbt } from "@/types"
import { MdModeEdit } from "react-icons/md"
import { RiDeleteBack2Line } from "react-icons/ri"
import toast from "react-hot-toast"
import { AxiosError } from "axios"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import DraftEditor from "@/components/DraftEditor"
import { convertFromRaw, convertToRaw, EditorState } from "draft-js"
import { getListJalurJenis } from "@/services/getData"

const postData = async (newData: TahapanCbt) => {
  const response = await api.post(
    "/setting/keterangan-tahapan-cbt/store",
    newData
  )
  return response.data
}

const patchData = async (newData: TahapanCbt) => {
  const response = await api.patch(
    "/setting/keterangan-tahapan-cbt/update",
    newData
  )
  return response.data
}

const deleteData = async (deletedData: {
  id_ref_keterangan_tahapan_cbt: string
}) => {
  const response = await api({
    url: "/setting/keterangan-tahapan-cbt/delete",
    method: "DELETE",
    data: deletedData,
  })
  return response.data
}

export default function Form({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE"
  selectedRow?: TahapanCbt | null
  closeModal: () => void
}) {
  const { data: dataJalurJenis } = useQuery({
    queryKey: ["listJalurJenis"],
    queryFn: getListJalurJenis,
  })
  const filteredJalurJenis: JalurJenis[] = dataJalurJenis?.filter(
    (item: JalurJenis) => {
      return item.is_cbt_internal
    }
  )

  const contentState = (() => {
    if (!selectedRow?.keterangan_nama_tahapan) return null;
    try {
      return convertFromRaw(JSON.parse(selectedRow.keterangan_nama_tahapan));
    } catch {
      return null;
    }
  })();

  const [editorState, setEditorState] = useState(
    contentState
      ? EditorState.createWithContent(contentState)
      : EditorState.createEmpty()
  );
  const [form, setForm] = useState<TahapanCbt>({
    id_ref_keterangan_tahapan_cbt:
      selectedRow?.id_ref_keterangan_tahapan_cbt || "",
    id_ref_jalur_jenis: selectedRow?.id_ref_jalur_jenis || "",
    keterangan_nama_tahapan: selectedRow?.keterangan_nama_tahapan || "",
    keterangan_waktu_tahapan_sesi_1:
      selectedRow?.keterangan_waktu_tahapan_sesi_1 || "",
    keterangan_waktu_tahapan_sesi_2:
      selectedRow?.keterangan_waktu_tahapan_sesi_2 || "",
    urutan: selectedRow?.urutan || "",
  })

  const isPost = method === "POST"
  const isPatch = method === "PATCH"
  const isDelete = method === "DELETE"

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listTahapanCbt"] })
      closeModal()
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const rawContent = convertToRaw(editorState.getCurrentContent())
    const jsonString = JSON.stringify(rawContent)

    mutate({ ...form, keterangan_nama_tahapan: jsonString })
  }

  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Delete Data Tahapan CBT"
          description={`Apakah anda yakin akan menghapus data tahapan CBT "${selectedRow?.id_ref_keterangan_tahapan_cbt}"?`}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </div>
      </form>
    )

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={isPost ? "Insert Data Tahapan CBT" : "Update Data Tahapan CBT"}
      />

      <div>
        <Label>Jalur Jenis</Label>
        <Select
          value={form.id_ref_jalur_jenis}
          onValueChange={(value) =>
            setForm((prev) => ({
              ...prev,
              id_ref_jalur_jenis: value,
            }))
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Pilih Jalur Jenis">
              {form.id_ref_jalur_jenis &&
                filteredJalurJenis?.find(
                  (jalurJenis: JalurJenis) =>
                    jalurJenis.id_ref_jalur_jenis === form.id_ref_jalur_jenis
                )?.nama_jalur_jenis}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {filteredJalurJenis?.map(
              (jalurJenis: JalurJenis, index: number) => (
                <SelectItem
                  key={index}
                  value={`${jalurJenis.id_ref_jalur_jenis}`}
                >
                  {jalurJenis?.nama_jalur_jenis}
                </SelectItem>
              )
            )}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="mb-1.5 block">Keterangan Nama Tahapan</Label>
        <DraftEditor
          editorState={editorState}
          onChange={setEditorState}
          placeholder="Ketik keterangan nama tahapan..."
        />
      </div>
      <div>
        <Label>Keterangan Waktu Tahapan Sesi 1</Label>
        <Input
          placeholder="Ketik keterangan waktu tahapan sesi 1..."
          value={form.keterangan_waktu_tahapan_sesi_1}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              keterangan_waktu_tahapan_sesi_1: event.target.value,
            }))
          }
        />
      </div>
      <div>
        <Label>Keterangan Waktu Tahapan Sesi 2</Label>
        <Input
          placeholder="Ketik keterangan waktu tahapan sesi 2..."
          value={form.keterangan_waktu_tahapan_sesi_2}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              keterangan_waktu_tahapan_sesi_2: event.target.value,
            }))
          }
        />
      </div>
      <div>
        <Label>Urutan</Label>
        <Input
          placeholder="Ketik urutan..."
          value={form.urutan}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              urutan: event.target.value,
            }))
          }
        />
      </div>

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  )
}
